import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useRobotContext } from './RobotContext';
import { GIF_ASSETS, getRandomInt } from './utils';
export function RobotBottomBar() {
    const { mode, images, imagesLoaded, onRobotClick, waveConfig, assetsBasePath } = useRobotContext();
    const [walkState, setWalkState] = useState('idle');
    const [direction, setDirection] = useState('right');
    const [position, setPosition] = useState(0);
    const [isWaving, setIsWaving] = useState(false);
    const [wavingGif, setWavingGif] = useState('waving');
    const [standingGif, setStandingGif] = useState('standing_one');
    const containerRef = useRef(null);
    const robotRef = useRef(null);
    const animationFrameRef = useRef(null);
    const walkStartTimeRef = useRef(0);
    const waveTimeoutRef = useRef(null);
    const pauseTimeoutRef = useRef(null);
    const isAnimatingRef = useRef(false);
    const gifLoopDurationRef = useRef(1500); // Default: 1.5 seconds per GIF loop (adjust based on your GIF)
    const walkStartTimeWalkRef = useRef(0);
    const plannedStopsRef = useRef([]); // Progress points (0-1) where robot should stop
    const currentPausePositionRef = useRef(0); // Position where robot paused during walk
    const standingTimeoutRef = useRef(null);
    useEffect(() => {
        if (!imagesLoaded || !containerRef.current)
            return;
        const startWalking = () => {
            setWalkState('walk-start');
            walkStartTimeRef.current = Date.now();
        };
        // Initial start
        startWalking();
    }, [imagesLoaded]);
    // Separate effect for walk-loop animation to avoid cleanup issues
    useEffect(() => {
        if (!imagesLoaded || walkState !== 'walk-loop') {
            // Stop animation if not in walk-loop
            if (isAnimatingRef.current) {
                isAnimatingRef.current = false;
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                    animationFrameRef.current = undefined;
                }
            }
            return;
        }
        const container = containerRef.current;
        if (!container)
            return;
        // Wait a bit for container to be ready
        const startAnimation = () => {
            var _a;
            const containerWidth = container.offsetWidth;
            const robotWidth = ((_a = robotRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 210;
            const maxPosition = Math.max(0, containerWidth - robotWidth);
            // Cancel any existing animation before starting new one
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            // Calculate initial and target positions
            // If resuming from a pause, use the paused position; otherwise start from edge
            const resumePosition = currentPausePositionRef.current > 0 ? currentPausePositionRef.current : null;
            const startPosition = resumePosition !== null && resumePosition !== void 0 ? resumePosition : (direction === 'right' ? 0 : maxPosition);
            const targetPosition = direction === 'right' ? maxPosition : 0;
            const totalDistance = Math.abs(targetPosition - startPosition);
            // Set initial position
            setPosition(startPosition);
            // If resuming, clear the pause position and keep remaining stops
            // Otherwise, plan new stops
            if (!resumePosition) {
                currentPausePositionRef.current = 0;
                // Plan 5-6 random stops during the walk (between 10% and 90% of the journey)
                const numStops = Math.random() < 0.5 ? 5 : 6; // 50% chance of 5 stops, 50% chance of 6 stops
                plannedStopsRef.current = [];
                for (let i = 0; i < numStops; i++) {
                    // Random progress point between 0.1 and 0.9, distributed more evenly
                    const stopProgress = 0.1 + Math.random() * 0.8;
                    plannedStopsRef.current.push(stopProgress);
                }
                // Sort stops in order
                plannedStopsRef.current.sort((a, b) => a - b);
            }
            else {
                // When resuming, adjust remaining stops based on current progress
                // Calculate how much progress we've already made
                const originalStart = direction === 'right' ? 0 : maxPosition;
                const originalDistance = maxPosition;
                const currentProgress = direction === 'right'
                    ? (startPosition - originalStart) / originalDistance
                    : (originalStart - startPosition) / originalDistance;
                // Filter out stops we've already passed and adjust remaining ones
                plannedStopsRef.current = plannedStopsRef.current
                    .filter((stop) => stop > currentProgress)
                    .map((stop) => (stop - currentProgress) / (1 - currentProgress)); // Normalize to 0-1 range
            }
            // Calculate how many GIF loops we want during the walk
            // Increased speed: fewer loops for faster movement (6-10 loops instead of 8-12)
            const desiredGifLoops = Math.max(6, Math.floor(totalDistance / 60)); // At least 6 loops, more for longer distances
            const totalWalkDuration = desiredGifLoops * gifLoopDurationRef.current; // Total time in ms
            isAnimatingRef.current = true;
            walkStartTimeWalkRef.current = Date.now();
            // Clear pause position after using it
            if (resumePosition) {
                currentPausePositionRef.current = 0;
            }
            const animate = () => {
                if (!isAnimatingRef.current)
                    return; // Stop if animation was cancelled
                const currentTime = Date.now();
                const elapsed = currentTime - walkStartTimeWalkRef.current;
                // Calculate progress: 0 to 1 based on elapsed time
                const progress = Math.min(elapsed / totalWalkDuration, 1);
                // Check if we've reached a planned stop (stops are relative to remaining journey)
                if (plannedStopsRef.current.length > 0 && progress >= plannedStopsRef.current[0]) {
                    // Reached a stop point - pause and show standing
                    const stopProgress = plannedStopsRef.current.shift(); // Remove first stop
                    const stopPosition = direction === 'right'
                        ? startPosition + totalDistance * stopProgress
                        : startPosition - totalDistance * stopProgress;
                    currentPausePositionRef.current = stopPosition;
                    isAnimatingRef.current = false;
                    // Show waving animation (cycle through waving, waving_2, waving_3)
                    const wavingGifs = [
                        'waving',
                        'waving_2',
                        'waving_3',
                    ];
                    const randomWavingGif = wavingGifs[Math.floor(Math.random() * wavingGifs.length)];
                    setWavingGif(randomWavingGif);
                    setWalkState('standing-during-walk');
                    setPosition(stopPosition);
                    return;
                }
                // Calculate position based on time, not frame count (more accurate)
                const newPos = direction === 'right'
                    ? startPosition + totalDistance * progress
                    : startPosition - totalDistance * progress;
                // Check if we've reached the target or completed desired GIF loops
                const reachedTarget = direction === 'right' ? newPos >= targetPosition - 0.5 : newPos <= 0.5;
                // Also check if we've completed the desired number of GIF loops
                const completedLoops = Math.floor(elapsed / gifLoopDurationRef.current);
                const shouldStop = reachedTarget || completedLoops >= desiredGifLoops;
                if (shouldStop) {
                    // Reached the edge or completed loops, stop animation and pause
                    isAnimatingRef.current = false;
                    setWalkState('paused');
                    setPosition(direction === 'right' ? targetPosition : 0);
                    return;
                }
                // Update position
                setPosition(newPos);
                // Continue animation
                animationFrameRef.current = requestAnimationFrame(animate);
            };
            // Start the animation immediately
            animationFrameRef.current = requestAnimationFrame(animate);
        };
        // Small delay to ensure container is ready
        const timeoutId = setTimeout(startAnimation, 10);
        return () => {
            clearTimeout(timeoutId);
            isAnimatingRef.current = false;
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = undefined;
            }
        };
    }, [walkState, direction, imagesLoaded]);
    useEffect(() => {
        if (!imagesLoaded || walkState === 'idle')
            return;
        const container = containerRef.current;
        if (!container)
            return;
        const handleWalkStart = () => {
            // Play walking_start.gif once, then transition to walk-loop
            const startGif = images.get(GIF_ASSETS.walking_start);
            if (startGif) {
                const duration = 5000; // 5 seconds for walking_start animation
                setTimeout(() => {
                    setWalkState('walk-loop');
                }, duration);
            }
            else {
                setWalkState('walk-loop');
            }
        };
        const handlePaused = () => {
            // Cancel any existing pause timeout
            if (pauseTimeoutRef.current) {
                clearTimeout(pauseTimeoutRef.current);
            }
            // First show standing animation (at edge, use standing_one or standing_two)
            const useStandingOne = Math.random() < 0.5;
            setStandingGif(useStandingOne ? 'standing_one' : 'standing_two');
            setWalkState('standing');
        };
        const handleStanding = () => {
            // Cancel any existing pause timeout
            if (pauseTimeoutRef.current) {
                clearTimeout(pauseTimeoutRef.current);
            }
            // Show standing animation for 5 seconds
            const standingDuration = 5000;
            pauseTimeoutRef.current = setTimeout(() => {
                // Always switch direction when done standing (at edge)
                const currentDirection = direction;
                const newDirection = currentDirection === 'right' ? 'left' : 'right';
                // Update direction first
                setDirection(newDirection);
                // Decide: wave or walk back
                const shouldWave = Math.random() < 0.4; // 40% chance to wave
                if (shouldWave) {
                    setIsWaving(true);
                    setWalkState('wave');
                }
                else {
                    // Start walking in the new direction
                    setWalkState('walk-start');
                }
            }, standingDuration);
        };
        const handleStandingDuringWalk = () => {
            // Cancel any existing timeout
            if (standingTimeoutRef.current) {
                clearTimeout(standingTimeoutRef.current);
            }
            // Show waving animation for 5 seconds
            const standingDuration = 5000;
            standingTimeoutRef.current = setTimeout(() => {
                // Resume walking from where we paused (same direction)
                // Don't clear currentPausePositionRef yet - it will be used to resume
                setWalkState('walk-loop');
            }, standingDuration);
        };
        const handleWave = () => {
            // Cancel any existing wave timeout
            if (waveTimeoutRef.current) {
                clearTimeout(waveTimeoutRef.current);
            }
            const waveGif = images.get(GIF_ASSETS.waving);
            if (waveGif) {
                const waveDuration = 5000; // 5 seconds of waving
                setTimeout(() => {
                    setIsWaving(false);
                    // Wait random idle time before next walk
                    const idleTime = getRandomInt(waveConfig.minIdleMs, waveConfig.maxIdleMs);
                    waveTimeoutRef.current = setTimeout(() => {
                        // Direction should already be switched from paused state
                        // Just start walking
                        setWalkState('walk-start');
                    }, idleTime);
                }, waveDuration);
            }
            else {
                setIsWaving(false);
                // If no wave gif, just pause briefly then continue
                pauseTimeoutRef.current = setTimeout(() => {
                    // Direction should already be switched, just start walking
                    setWalkState('walk-start');
                }, 2000);
            }
        };
        switch (walkState) {
            case 'walk-start':
                handleWalkStart();
                break;
            case 'paused':
                handlePaused();
                break;
            case 'standing':
                handleStanding();
                break;
            case 'standing-during-walk':
                handleStandingDuringWalk();
                break;
            case 'wave':
                handleWave();
                break;
        }
        return () => {
            if (waveTimeoutRef.current) {
                clearTimeout(waveTimeoutRef.current);
            }
            if (pauseTimeoutRef.current) {
                clearTimeout(pauseTimeoutRef.current);
            }
            if (standingTimeoutRef.current) {
                clearTimeout(standingTimeoutRef.current);
            }
        };
    }, [walkState, imagesLoaded, images, waveConfig, direction]);
    if (mode !== 'bottom-bar') {
        return null;
    }
    if (!imagesLoaded) {
        return (_jsx("div", { style: {
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                height: '210px',
                backgroundColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
            } }));
    }
    const getCurrentGif = () => {
        if (isWaving) {
            return `${assetsBasePath}/${GIF_ASSETS.waving}`;
        }
        switch (walkState) {
            case 'walk-start':
                return `${assetsBasePath}/${GIF_ASSETS.walking_start}`;
            case 'walk-loop':
                // Keep looping the continuous walking gif while moving
                return `${assetsBasePath}/${GIF_ASSETS.walking_continous}`;
            case 'standing':
                // At edge, show standing_one or standing_two
                return `${assetsBasePath}/${GIF_ASSETS[standingGif]}`;
            case 'standing-during-walk':
                // During walk, show waving animations (waving, waving_2, or waving_3)
                return `${assetsBasePath}/${GIF_ASSETS[wavingGif]}`;
            case 'paused':
                // When paused at edge, show walking_end
                return `${assetsBasePath}/${GIF_ASSETS.walking_end}`;
            default:
                // Default to continuous walking animation
                return `${assetsBasePath}/${GIF_ASSETS.walking_continous}`;
        }
    };
    return (_jsxs("div", { ref: containerRef, style: {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: '210px',
            backgroundColor: 'transparent',
            zIndex: 1000,
            overflow: 'hidden',
        }, children: [_jsx("style", { children: `
          @keyframes cursorClick {
            0% {
              transform: translate(25px, 25px);
              opacity: 0;
            }
            15% {
              transform: translate(0px, 0px);
              opacity: 1;
            }
            25% {
              transform: translate(0px, 0px) scale(0.85);
              opacity: 1;
            }
            35% {
              transform: translate(0px, 0px) scale(1);
              opacity: 1;
            }
            60% {
              transform: translate(25px, 25px);
              opacity: 0;
            }
            100% {
              transform: translate(25px, 25px);
              opacity: 0;
            }
          }
          @keyframes clickRipple {
            0% {
              transform: translate(-50%, -50%) scale(0.2);
              opacity: 0;
            }
            24% {
              transform: translate(-50%, -50%) scale(0.2);
              opacity: 0;
            }
            25% {
              transform: translate(-50%, -50%) scale(0.2);
              opacity: 0.8;
            }
            50% {
              transform: translate(-50%, -50%) scale(1.5);
              opacity: 0;
            }
            100% {
              transform: translate(-50%, -50%) scale(1.5);
              opacity: 0;
            }
          }
        ` }), _jsxs("div", { onClick: onRobotClick, style: {
                    position: 'absolute',
                    bottom: 0,
                    left: `${position}px`,
                    width: '210px',
                    height: '210px',
                    cursor: 'pointer',
                    zIndex: 1000,
                }, children: [_jsx("img", { ref: robotRef, src: getCurrentGif(), alt: "Robot", style: {
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 2,
                            imageRendering: 'auto',
                            transform: walkState === 'standing' || walkState === 'wave' || walkState === 'paused'
                                ? 'scaleX(1)'
                                : direction === 'left'
                                    ? 'scaleX(-1)'
                                    : 'scaleX(1)',
                            transition: walkState === 'walk-loop' ? 'none' : 'transform 0.1s',
                        } }, walkState === 'walk-loop'
                        ? `walk-${direction}`
                        : walkState === 'standing-during-walk'
                            ? `waving-${wavingGif}`
                            : walkState === 'standing'
                                ? `standing-${standingGif}`
                                : walkState), _jsx("div", { style: {
                            position: 'absolute',
                            bottom: '70px',
                            left: '50%',
                            width: '40px',
                            height: '40px',
                            backgroundColor: 'rgba(100, 192, 197)',
                            borderRadius: '50%',
                            animation: 'clickRipple 3s ease-out infinite',
                            zIndex: 3,
                            pointerEvents: 'none',
                        } }), _jsx("div", { style: {
                            position: 'absolute',
                            bottom: '60px',
                            left: '50%',
                            width: '32px',
                            height: '32px',
                            animation: 'cursorClick 3s ease-in-out infinite',
                            zIndex: 4,
                            pointerEvents: 'none',
                        }, children: _jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32", viewBox: "0 0 24 24", fill: "white", stroke: "#0f172a", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", style: { filter: 'drop-shadow(0px 4px 4px rgba(0,0,0,0.4))' }, children: _jsx("path", { d: "M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" }) }) })] })] }));
}
